// One editable property row of a card (R-U6) and the rule of who may write it; the card, a tab of an app and the «дополните»
// dialog show fields through these rows.

import { useState } from 'react';
import { canWrite, recordClosedFor, type FieldDef, type Id, type RecordObj } from '@confdeck/core-shared';
import { FieldValue } from '../fields/FieldValue.tsx';
import { InlineEditor, PopoverEditor, POPOVER_FIELDS, WIDE_FIELDS } from '../fields/FieldEditor.tsx';
import { setField } from '../sync/mutations.ts';
import { useAccess, useMeta, useRecord, useSync } from '../sync/react.tsx';
import { FIELD_ICONS, Icon } from '../ui/icons.tsx';
import { Popover, PopoverAnchor, PopoverContent, Select, cx, titleIfCut } from '../ui/primitives.tsx';
import { ColResizer, LABEL_W } from '../ui/ColWidth.tsx';
import s from './record.module.css';

/** Writable (F4 audit №8, R-T4): the role writes the section and the record is not on a stage closed for this person (R-T4). */
export const useWritable = (rec: RecordObj) => {
  const access = useAccess();
  const meta = useMeta();
  const client = useSync();
  return canWrite(access, rec.typeId) && !recordClosedFor(meta, rec, meta.user(client.header?.userId));
};

/** Editable rows of chosen fields of a record: a tab of an app shows the fields its work needs in one place (J8). */
export function PropList({ recordId, fields }: { recordId: Id; fields: string[] }) {
  const meta = useMeta();
  const rec = useRecord(recordId);
  if (!rec) return null;
  const defs = fields.map((k) => meta.field(rec.typeId, k)).filter((x): x is FieldDef => !!x && !x.archivedAt);
  return <dl className={s.props}>{defs.map((f) => <Prop key={f.id} rec={rec} field={f} />)}<LabelEdge /></dl>;
}

/** One editable property row of a card; the «дополните» dialog shows the fields a stage asks for with it. */
export function Prop({ rec, field }: { rec: RecordObj; field: FieldDef }) {
  const client = useSync();
  const [editing, setEditing] = useState(false);
  const popover = POPOVER_FIELDS.has(field.fieldType);
  const empty = rec.data[field.key] == null || rec.data[field.key] === '' || (Array.isArray(rec.data[field.key]) && !(rec.data[field.key] as unknown[]).length);
  const writable = useWritable(rec);
  if (field.fieldType === 'checkbox') return <FlagProp rec={rec} field={field} writable={writable && !field.options.managed} />;
  const start = () => { if (field.options.managed || !writable) return; setEditing(true); };
  return (
    <>
      <dt className={s.label}><Icon name={FIELD_ICONS[field.fieldType] ?? 'text'} size={14} /><span className={s.labelText} onMouseEnter={titleIfCut}>{field.name}</span>{field.required && <span className={s.req}>*</span>}</dt>
      <dd className={cx(s.value, editing && s.valueEditing, field.options.managed && s.valueLocked)} title={field.options.managed ? 'Меняется действиями записи' : undefined} onClick={() => !editing && start()} tabIndex={editing ? -1 : 0}
        onKeyDown={(e) => { if (!editing && (e.key === 'Enter' || e.key === 'F2')) { e.preventDefault(); start(); } }}>
        {editing && !popover ? <InlineEditor rec={rec} field={field} onDone={() => setEditing(false)} /> : empty ? <span className={s.empty}>Пусто</span> : <FieldValue rec={rec} field={field} wrap />}
        {popover && (
          <Popover open={editing} onOpenChange={(o) => !o && setEditing(false)}>
            <PopoverAnchor className={s.anchor} />
            <PopoverContent width={WIDE_FIELDS.has(field.fieldType) ? undefined : 280}><PopoverEditor rec={rec} field={field} onDone={() => setEditing(false)} /></PopoverContent>
          </Popover>
        )}
      </dd>
    </>
  );
}

const YES_NO = [{ value: 'yes', label: 'Да' }, { value: 'no', label: 'Нет' }];

/**
 * A flag of a card is a choice of «Да» or «Нет» (R-U25, um/2026-10-31/03): a click no longer flips it unseen, and a flag never set
 * reads as «Нет» — there is no third, empty value.
 */
function FlagProp({ rec, field, writable }: { rec: RecordObj; field: FieldDef; writable: boolean }) {
  const client = useSync();
  const on = rec.data[field.key] === true;
  return (
    <>
      <dt className={s.label}><Icon name={FIELD_ICONS[field.fieldType] ?? 'text'} size={14} /><span className={s.labelText} onMouseEnter={titleIfCut}>{field.name}</span>{field.required && <span className={s.req}>*</span>}</dt>
      <dd className={cx(s.value, s.valueFlag, !writable && s.valueLocked)} title={field.options.managed ? 'Меняется действиями записи' : undefined}>
        {writable
          ? <Select className={s.flagSelect} label={`Да / Нет: ${field.name}`} value={on ? 'yes' : 'no'} options={YES_NO} onChange={(v) => { if ((v === 'yes') !== on) setField(client, rec, field.key, v === 'yes'); }} />
          : <span>{on ? 'Да' : 'Нет'}</span>}
      </dd>
    </>
  );
}

/** The edge between the labels and the values: one width of the labels for every card (R-U17). */
export const LabelEdge = () => <ColResizer width={LABEL_W} label="Ширина подписей" className={s.propsEdge} />;

/** The grid of property rows (`Prop`), for screens that show some fields of a record the card's way. */
export const propsClass = s.props;
